/**
 * Plain-text client summary for pasting into email, Slack or a ticket (#231).
 * Missing fields are omitted, never rendered as "null". API keys and webhook
 * credentials are deliberately excluded.
 */
import type {
  ClientDetailsResponse,
  ClientPlatformSummary,
  ClientUserResponse,
} from '@shared/types';

export interface ClientSummaryInput {
  clientID: string;
  name: string | null;
  timeZone: string | null;
  details: ClientDetailsResponse | null;
  contacts: ClientUserResponse[];
  encoders: { online: number; total: number };
  persistentStreamCount: number;
  platform: ClientPlatformSummary | null;
}

function joinPresent(parts: Array<string | null | undefined>, sep: string): string {
  return parts.filter((p): p is string => Boolean(p)).join(sep);
}

function section(title: string, lines: string[]): string[] {
  return lines.length > 0 ? ['', title, ...lines.map((l) => `  ${l}`)] : [];
}

export function buildClientSummary(input: ClientSummaryInput): string {
  const { details, platform } = input;
  const account = details?.accountInfo;
  const level = details?.package?.levelName;

  const header = [
    `${input.name ?? `Client ${input.clientID}`} (clientID ${input.clientID})`,
    joinPresent([level && `Level: ${level}`, input.timeZone && `TZ: ${input.timeZone}`], ' | '),
  ].filter(Boolean);

  const address = account
    ? joinPresent([account.city, joinPresent([account.state, account.zip], ' '), account.countryCode], ', ')
    : '';
  const accountLines = [
    account?.phone && `Phone: ${account.phone}`,
    address && `Address: ${address}`,
    account?.demoTTL && 'Demo account',
  ].filter((l): l is string => Boolean(l));

  const contactLines = input.contacts.map((c) => {
    const name = joinPresent([c.firstName, c.lastName], ' ');
    return name ? `${name} - ${c.email}` : c.email;
  });

  const platformLines = [
    `Encoders: ${input.encoders.online} online / ${input.encoders.total} total`,
    `24x7 streams: ${input.persistentStreamCount}`,
    platform && `Live events (12 mo): ${platform.liveEventCount}`,
    platform && platform.services.length > 0 &&
      `Services: ${platform.services.map((s) => s.label).join(', ')}`,
  ].filter((l): l is string => Boolean(l));

  return [
    ...header,
    ...section('ACCOUNT', accountLines),
    ...section('CONTACTS', contactLines),
    ...section('PLATFORM', platformLines),
  ].join('\n');
}
